'use client';

import { c as _c } from "react/compiler-runtime";
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import type { ComponentProps } from 'react';
import { useId } from 'react';
import { Explained } from './explained';
import { Switch } from '@/components/ui/switch';
import { BetterTooltip } from '@/components/ui/tooltip';

/** What the switch does, and nothing else: its hover text, and /code's label tooltip. */
export const AUTO_FIX_EXPLAINER = 'Validate issues found by cubic and failing CI checks, fix relevant ones, and push changes to this branch.';

/** Why the switch cannot be used, for the reasons a reader can act on. */
const INELIGIBLE_REASONS: Record<string, string> = {
  fork: 'Not available on pull requests from forks',
  auto_fix_disabled: 'Auto-fix is turned off for this repository',
  fix_with_cubic_disabled: 'Fix with cubic is disabled for this repository',
  provider_not_cubic: 'Runs on the cubic coding agent only',
  installation_not_found: 'cubic could not find the GitHub installation for this repository',
  unavailable: 'Eligibility could not be checked right now',
  pr_not_open: 'This pull request is not open',
  pr_not_found: 'This pull request could not be found'
};
export type AutoFixLoopSwitchState = {
  checked: boolean;
  disabled: boolean;
  /** Why a disabled switch cannot be used, when cubic gave a reason a reader can act on. */
  disabledReason: string | null;
};

/**
 * The switch for a loop's status, or null on a pull request that cannot run auto-fix and never
 * did. Once the loop has run, the switch stays, disabled, with the reason on hover.
 */
export function resolveAutoFixLoopSwitchState(data: AutoFixLoopStatusView): AutoFixLoopSwitchState | null {
  const eligible = data.eligibility?.eligible ?? false;
  if (!data.enabled && data.iterations.length === 0 && !eligible) {
    return null;
  }
  const disabled = !data.enabled && !eligible;
  const reason = disabled ? data.eligibility?.reason : null;
  return {
    checked: data.enabled,
    disabled,
    disabledReason: reason ? INELIGIBLE_REASONS[reason] ?? null : null
  };
}

/** The auto-fix switch. A disabled one says why on hover. */
export function AutoFixLoopSwitch(t0) {
  const $ = _c(17);
  const {
    id,
    state,
    onCheckedChange,
    explain: t1,
    size
  } = t0;
  const explain = t1 === undefined ? false : t1;
  const explainerId = useId();
  const explains = explain && !state.disabledReason;
  const t2 = state.checked ? "Turn off auto-fix" : "Turn on auto-fix";
  const t3 = explains ? explainerId : undefined;
  const t4 = state.disabledReason ? "pointer-events-none" : undefined;
  let t5;
  if ($[0] !== id || $[1] !== onCheckedChange || $[2] !== size || $[3] !== state.checked || $[4] !== state.disabled || $[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <Switch aria-label={t2} aria-describedby={t3} checked={state.checked} className={t4} disabled={state.disabled} id={id} size={size} onCheckedChange={onCheckedChange} />;
    $[0] = id;
    $[1] = onCheckedChange;
    $[2] = size;
    $[3] = state.checked;
    $[4] = state.disabled;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const autoFixSwitch = t5;
  if (explains) {
    let t6;
    if ($[9] !== explainerId) {
      t6 = <span id={explainerId} hidden={true}>{AUTO_FIX_EXPLAINER}</span>;
      $[9] = explainerId;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== autoFixSwitch || $[12] !== t6) {
      t7 = <BetterTooltip side="top" align="end" contentClassName="max-w-[220px] text-xs" content={AUTO_FIX_EXPLAINER}><span className="inline-flex">{autoFixSwitch}{t6}</span></BetterTooltip>;
      $[11] = autoFixSwitch;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  if (!state.disabledReason) {
    return autoFixSwitch;
  }
  let t6;
  if ($[14] !== autoFixSwitch || $[15] !== state.disabledReason) {
    t6 = <Explained text={state.disabledReason} align="end" role="group" aria-label="Auto-fix" aria-disabled="true" className="inline-flex cursor-not-allowed" data-testid="auto-fix-loop-disabled-reason">{autoFixSwitch}</Explained>;
    $[14] = autoFixSwitch;
    $[15] = state.disabledReason;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}